import { useState } from "react";
import { nearbyAttractions } from "../config/siteData";
import { MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const AttractionCard = ({ attraction, translatedData }) => {
  const [currentImage, setCurrentImage] = useState(0);

  const nextImage = () => {
    setCurrentImage((prev) => 
      prev === attraction.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImage((prev) => 
      prev === 0 ? attraction.images.length - 1 : prev - 1
    );
  };

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#e2e8f0] feature-card"
      data-testid={`attraction-card-${attraction.id}`}
    >
      {/* Mini Gallery */}
      <div className="relative h-48 md:h-56 overflow-hidden group">
        {attraction.images.map((image, index) => (
          <img
            key={index}
            src={image}
            alt={`${translatedData.name} - ${index + 1}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
            style={{ opacity: index === currentImage ? 1 : 0 }}
            loading="lazy"
          />
        ))}
        
        {/* Navigation Arrows */}
        {attraction.images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/60"
              aria-label="Previous image"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/60"
              aria-label="Next image"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
        
        {/* Image Dots */}
        {attraction.images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {attraction.images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  index === currentImage
                    ? "bg-white w-4"
                    : "bg-white/60"
                }`}
                aria-label={`Image ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Distance Badge */}
        <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-[#004E64] text-white text-xs font-medium flex items-center gap-1.5">
          <MapPin size={12} />
          {translatedData.distance}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-semibold font-['Outfit'] text-[#0F172A] mb-2">
          {translatedData.name}
        </h3>
        <p className="text-sm text-[#64748b] leading-relaxed">
          {translatedData.description}
        </p>
      </div>
    </div>
  );
};

export const Attractions = () => {
  const { t } = useLanguage();
  
  // Map attraction numeric IDs to translation keys
  const attractionKeyMap = {
    1: 'orebic',
    2: 'viganj',
    3: 'korcula',
    4: 'mljet',
    5: 'svIlija',
    6: 'dubrovnik',
  };
  
  return (
    <section
      id="atrakcije"
      className="py-16 md:py-24 bg-[#FAFAF9]"
      data-testid="attractions-section"
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-[#D4A373] mb-3 block">
            {t.attractions.subtitle}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-[#0F172A] mb-4"
            data-testid="attractions-title"
          >
            {t.attractions.title}
          </h2>
          <p className="text-base md:text-lg text-[#64748b] max-w-2xl mx-auto">
            {t.attractions.description}
          </p>
        </div>

        {/* Attractions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {nearbyAttractions.map((attraction) => {
            const translationKey = attractionKeyMap[attraction.id];
            const translatedData = translationKey && t.attractions.places[translationKey] 
              ? t.attractions.places[translationKey]
              : {
                  name: attraction.name,
                  description: attraction.description,
                  distance: attraction.distance,
                };
            
            return (
              <AttractionCard 
                key={attraction.id} 
                attraction={attraction} 
                translatedData={translatedData}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};
